import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Logo } from "../components/Logo";
import { LanguageSelect } from "../components/LanguageSelect";
import type { DataStore } from "../data";

/** Official download of the free, open source SMS Gateway for Android app (Apache 2.0). */
export const SMS_APP_DOWNLOAD = "https://github.com/capcom6/android-sms-gateway/releases/latest";

/** "#phone-setup/<code>": opened on the workshop's Android phone from the QR code, no sign in. */
export const phoneSetupTokenFromHash = (): string | null => {
  const m = /^#phone-setup\/([0-9a-f]{32,})$/i.exec(globalThis.location?.hash ?? "");
  return m ? m[1]! : null;
};

export function PhoneSetup({ store, token }: { store: DataStore; token: string }) {
  const { t } = useTranslation();
  const [workshop, setWorkshop] = useState<string | null | undefined>(undefined);
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [done, setDone] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => void store.smsSetupInfo(token).then(setWorkshop, () => setWorkshop(null)), [store, token]);

  const save = async () => {
    setFailed(false);
    try {
      await store.completeSmsSetup(token, user, password);
      setDone(true);
    } catch {
      setFailed(true);
    }
  };

  return (
    <div className="phone-setup">
      <div className="brand">
        <Logo size={32} />
      </div>
      <h1>{t("phoneSetup.title")}</h1>
      {workshop === undefined && <div className="loading" />}
      {workshop === null && !done && <p className="warn">{t("phoneSetup.invalid")}</p>}
      {done && <p className="ok-band">{t("phoneSetup.done")}</p>}
      {workshop && !done && (
        <>
          <p>{t("phoneSetup.intro", { workshop })}</p>
          <ol className="setup-steps">
            <li>
              <strong>{t("phoneSetup.step1")}</strong>
              <p>{t("phoneSetup.step1Text")}</p>
              <a className="button primary" href={SMS_APP_DOWNLOAD} target="_blank" rel="noreferrer">
                {t("phoneSetup.download")}
              </a>
            </li>
            <li>
              <strong>{t("phoneSetup.step2")}</strong>
              <p>{t("phoneSetup.step2Text")}</p>
            </li>
            <li>
              <strong>{t("phoneSetup.step3")}</strong>
              <p>{t("phoneSetup.step3Text")}</p>
            </li>
            <li>
              <strong>{t("phoneSetup.step4")}</strong>
              <p>{t("phoneSetup.step4Text")}</p>
              <label className="field wide">
                <span>{t("phoneSetup.user")}</span>
                <input value={user} onChange={(e) => setUser(e.target.value)} autoComplete="off" autoCapitalize="none" aria-label={t("phoneSetup.user")} />
              </label>
              <label className="field wide">
                <span>{t("phoneSetup.password")}</span>
                <input value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="off" autoCapitalize="none" aria-label={t("phoneSetup.password")} />
              </label>
              {failed && <p className="missing">{t("phoneSetup.failed")}</p>}
              <button type="button" className="primary" disabled={!user.trim() || !password.trim()} onClick={() => void save()}>
                {t("phoneSetup.save")}
              </button>
            </li>
          </ol>
        </>
      )}
      <LanguageSelect />
    </div>
  );
}
